import React, { useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { downloadExport } from "@/lib/api";
import api, { apiErr, assetUrl, receiptUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHelpPanel } from "@/components/PageHelpPanel";
import { PageHeader, Card, EmptyState, Btn, InfoTip } from "@/components/common";
import { StatusChip } from "@/components/StatusChip";
import ExpenseDialog from "@/components/ExpenseDialog";
import { AddCategoryInline, ADD_CATEGORY_OPTION, ADD_CATEGORY_LABEL, NO_FAMILY_CATEGORIES_HINT } from "@/components/AddCategoryInline";
import { money, categoryColor, taxYears, STATUS_META, fmtDate, EXPENSE_GROUPS, localISO, AOTC_ELIGIBLE_CATEGORIES, AOTC_COMPUTER_GATE_CATEGORIES } from "@/lib/format";
import { categoryChoicesFor, irsCategoriesFor, ALL_IRS_CATEGORIES } from "@/lib/categoryGate";
import { MoneyInput } from "@/lib/accountingFormat";
import { useFmt } from "@/lib/useFmt";
import { useParties, expenseRoutingLabel } from "@/lib/parties";
import { usePaymentSources, psLabel, psById, psOwnerName } from "@/lib/payments";
import { isAwaitingARate, notInTheseTotals, NO_DOLLAR_AMOUNT_FILTER, AWAITING_A_RATE_BADGE, AWAITING_A_RATE_HOVER, NEEDS_A_RATE_BADGE, NEEDS_A_RATE_HOVER } from "@/lib/fxPanel";
import { splitExpenseTabs, pricedSum, needsAManualRate, isNotYetPaid, TAB_529, TAB_NON529, TAB_NEEDS_DECISION } from "@/lib/expenseTabs";
import { forTheSelectedBeneficiary, archivedBeneficiaryIds } from "@/lib/whoseWorkIsWaiting";
import { useExpensesOpeningYear } from "@/lib/startingTaxYear";
import { useStudentFromAddress, STUDENT_PARAM } from "@/lib/studentFromAddress";
import { ATTENTION_PARAM, ATTENTION_ONLY_CHIP, useAttention, countedBy } from "@/lib/attentionLanding";
import { useWithdrawThisAmount, withdrawNavState, NEXT_MONTH_NOTE, DECEMBER_NOTE } from "@/lib/withdrawThisAmount";
import { Plus, CurrencyDollar, Trash, PencilSimple, Receipt, DownloadSimple, FilePdf, Question, CaretDown, CaretUp, ArrowsDownUp, ListChecks, X, Funnel, EyeSlash, CheckCircle, Warning } from "@phosphor-icons/react";
import { CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { HomeCurrencyEstimate, useHomeEstimate } from "@/components/HomeCurrencyEstimate";
import { AttachmentButton, AttachmentPanel, AttachmentGap, AttachmentHelp, useAttachmentPanel, useReceiptIndex } from "@/components/AttachmentView";
import { ViewButton, RecordView } from "@/components/RecordView";
import { expenseFields, RECORD_TITLES } from "@/lib/recordFields";
import { useStudentWithdrawals, untiedForYear, editWithdrawalNavState } from "@/lib/untiedWithdrawals";
import { tieFirstPlan, withdrawLeftNavState, rowsOfYear, sameYearUntied, expensesReminderWords } from "@/lib/tieFirst";
import { comingUpWindow } from "@/components/ComingUpCard";
import { UntiedWithdrawalReminder } from "@/components/UntiedWithdrawalReminder";
import { AOTC_ROW_TIP, aotcTotalInfo } from "@/lib/aotcListWords";
import { OverfundedExplanation } from "@/components/OverfundedExplanation";

/**
 * An expense's attachments, turned into something the panel can show. (ZEN-351)
 *
 * The server hands over a mixed list: files uploaded straight onto the expense,
 * and receipts filed against it. Both are already reduced to a name and an API
 * path by the one serializer that strips storage keys (ZEN-79), so nothing here
 * needs to know where the bytes live.
 *
 * A receipt is marked as one because the server can render a receipt PDF page
 * by page as pictures and cannot do that for an uploaded document — which is
 * what makes a scanned multi-page receipt readable on a phone.
 *
 * A NULL path is not an error. It is a receipt that arrived as the text of an
 * email with nothing attached, and the panel says exactly that rather than
 * showing an empty frame.
 */
// The footer's first line. Wording approved by Maryse, 17 Oct 2026. (ZEN-785)
const listedLabel = (n) => `${n} expense${n === 1 ? "" : "s"} on this list`;

function expenseAttachments(e, receiptIndex) {
  return (e.attachments || []).map((a, i) => {
    const isReceipt = a.source === "receipt";
    const rec = isReceipt ? receiptIndex?.[a.id] : null;
    return {
      key: `${e.id}:${a.id}`,
      label: a.filename || `Attachment ${i + 1}`,
      fileUrl: a.path ? assetUrl(a.path) : null,
      receiptId: isReceipt ? a.id : undefined,
      pageCount: rec?.page_count || 1,
      noFileNote: "This receipt arrived as the text of an email with no file attached, so there is nothing to display.",
    };
  });
}

// What the attachment is being checked AGAINST, named for anyone who cannot
// see the row it sits next to.
export function expenseLabel(e) {
  return `${e.payee || "expense"}, ${money(e.usd_amount, "USD")} on ${fmtDate(e.transaction_date)}`;
}

/** The 529 tab's note with "All tax years" picked, in place of the Awaiting
 *  totals. Wording approved by Maryse, 21 Oct 2026 (ZEN-792); the Help Desk
 *  sheet stays as it is. */
function AllYearsNote({ testid }) {
  return (
    <p className="text-xs text-[#374151]" data-testid={testid}>
      Pick a tax year above to see what can be withdrawn for it. Expenses from a past year should already have been withdrawn for in that year.{" "}
      <Link to="/help?article=same-year-rule" className="font-semibold text-[#034AA2] hover:underline" data-testid={`${testid}-help`}>See “The same-calendar-year rule” in the Help Desk →</Link>
    </p>
  );
}

export default function Expenses() {
  const { user, selectedStudentId } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const effStudent = user.role === "student" ? user.student_id : selectedStudentId;
  // Opens on the current tax year, or the most recent one with anything in it,
  // unless the address says otherwise; then whatever is picked. (ZEN-762)
  const [pickedYear, setYear] = useState(null);
  const [status, setStatus] = useState("");
  const [eligibleOnly, setEligibleOnly] = useState(false);
  const [hideReconciled, setHideReconciled] = useState(false);
  const [sortBy, setSortBy] = useState("date");
  // Newest first: the oldest are mostly settled and closed. (ZEN-762)
  const [sortDir, setSortDir] = useState("desc");
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  // The record behind the eye — read only, drawn from what this page already
  // holds. Not gated on any role: the row is already on this person's screen.
  // (ZEN-702)
  const [viewing, setViewing] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [splitTarget, setSplitTarget] = useState(null);
  // Checking a receipt against the row it belongs to is the core loop of this
  // page; it used to cost an edit-and-dig and buried the row behind a window.
  // The panel opens BENEATH the row instead, so the payee, the amount, the
  // currency and the status stay on screen. (ZEN-351)
  const attachPanel = useAttachmentPanel();
  const receiptIndex = useReceiptIndex(!!attachPanel.openKey);
  const [activeTab, setActiveTab] = useState("529");
  const [missingReceiptOnly, setMissingReceiptOnly] = useState(false);
  const [needsRateOnly, setNeedsRateOnly] = useState(false);
  // Which Overview warning sent us here, and the expenses it counted — kept
  // from the moment of landing, so an expense fixed here stays on screen
  // showing its change rather than vanishing. (ZEN-768)
  const [attentionKey, setAttentionKey] = useState(() => searchParams.get(ATTENTION_PARAM));
  const [attentionIds, setAttentionIds] = useState(null);
  // A link that names a beneficiary moves the picker to them, then clears the
  // whole address in one place. (ZEN-697, ZEN-768)
  const { settling } = useStudentFromAddress({
    alsoClear: ["tab", "tax_year", "missing_receipt", "needs_rate", ATTENTION_PARAM, "open"] });

  // ── URL param handling (deep-link from Year-End Readiness, etc.) ──
  React.useEffect(() => {
    const pTab = searchParams.get("tab");
    const pYear = searchParams.get("tax_year");
    const pMissing = searchParams.get("missing_receipt");
    // needs_rate is how the Overview's "show me which one" link lands the owner
    // on the expense that could not be priced. Without it that note names a
    // problem and drops them into a list of every expense to hunt through —
    // which is how a warning gets ignored. Same shape as needs_decision above.
    // (ZEN-632)
    const pNeedsRate = searchParams.get("needs_rate");
    const pAttention = searchParams.get(ATTENTION_PARAM);
    // open=<expense id> opens that expense's window — how the withdrawal form's
    // "Open the expense to pay the rest outside the 529" lands. Read from the
    // address before it is cleared below, and the expense is fetched by id, so
    // the window opens whatever filters this page starts on. (ZEN-842)
    const pOpen = searchParams.get("open");
    let changed = false;
    if (pOpen) {
      changed = true;
      api.get(`/expenses/${encodeURIComponent(pOpen)}`)
        .then(({ data }) => { if (data && data.id) { setEditing(data); setDlg(true); } })
        .catch((e) => toast.error(apiErr(e) || "That expense could not be opened."));
    }
    // needs_decision is how the Overview's "Review now" button lands the owner
    // in the waiting room; without it that button drops them on the 529 tab and
    // leaves them to hunt. Unknown values are still ignored. (ZEN-113)
    if (pTab === TAB_529 || pTab === TAB_NON529 || pTab === TAB_NEEDS_DECISION) { setActiveTab(pTab); changed = true; }
    if (pYear) { setYear(pYear); changed = true; }
    if (pMissing === "1") { setMissingReceiptOnly(true); changed = true; }
    if (pNeedsRate === "1") { setNeedsRateOnly(true); changed = true; }
    if (pAttention) changed = true;
    // With a beneficiary named, the hook above clears the address once the
    // picker has moved; clearing it here first would lose the name.
    if (changed && !searchParams.get(STUDENT_PARAM)) setSearchParams({}, { replace: true });
  }, []); // eslint-disable-line

  const openingYear = useExpensesOpeningYear(searchParams, effStudent);
  const yearDecided = pickedYear != null || openingYear !== undefined;
  const year = pickedYear ?? openingYear ?? "";
  // A warning's list is exactly the expenses it counted, whatever their year
  // or status, so while it shows the list is asked for across both and the
  // two pickers are put away. (ZEN-768)
  const listYear = attentionKey ? "" : year;
  // "All tax years" picked: the Awaiting totals would add several years
  // together, so the 529 tab shows one note instead (ZEN-792). Not while a
  // warning's list shows, which puts the year picker away.
  const allYears = !year && !attentionKey;
  const listStatus = attentionKey ? "" : status;
  const key = ["expenses", effStudent || "all", listYear, listStatus];
  const { data: expenses = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: async () => {
      const p = new URLSearchParams();
      if (effStudent) p.set("student_id", effStudent);
      if (listYear) p.set("tax_year", listYear);
      // Ask the server to leave the unclassified ones out. They were fetched
      // and then discarded here, which is not merely wasteful: the endpoint
      // returns at most 2,000 rows, so drafts could fill that budget and push
      // SETTLED expenses off the end — silently shrinking both tab totals. The
      // waiting room has its own request.
      //
      // Only when no status filter is set, because the server lets
      // include_drafts OVERWRITE an explicit status rather than combine with
      // it — sending both would silently turn the status filter off. With a
      // status chosen the cap is moot anyway, since "Draft" is no longer one of
      // the choices. (ZEN-113)
      if (listStatus) p.set("status", listStatus);
      else p.set("include_drafts", "false");
      return (await api.get(`/expenses?${p}`)).data;
    },
    // Not until the opening year is known, or the page would first load every
    // year and then load again. (ZEN-762) Nor while a linked beneficiary is
    // still being picked, or everyone's expenses would flash first. (ZEN-768)
    enabled: yearDecided && !settling,
  });
  const { data: attention, isError: attentionFailed } = useAttention(effStudent, { enabled: !!attentionKey && !settling });
  React.useEffect(() => {
    // If the list cannot be fetched, show every expense with no chip — never
    // a chip over an empty page claiming nothing is wrong.
    if (attentionKey && attentionFailed) { setAttentionKey(null); return; }
    if (attentionKey && attentionIds === null && !settling) {
      const ids = countedBy(attention, attentionKey);
      if (ids) setAttentionIds(ids);
    }
  }, [attention, attentionKey, attentionIds, settling, attentionFailed]);
  const onlyIds = attentionKey ? (attentionIds || []) : null;
  // Choosing someone else in the picker ends the warning's list: its
  // expenses belong to the person it opened on.
  const landedOn = React.useRef(undefined);
  React.useEffect(() => {
    if (!attentionKey || settling) return;
    if (landedOn.current === undefined) { landedOn.current = effStudent || null; return; }
    if ((effStudent || null) !== landedOn.current) { setAttentionKey(null); setAttentionIds(null); }
  }, [effStudent, attentionKey, settling]);
  const { data: studentObj } = useQuery({ queryKey: ["student", effStudent], queryFn: async () => (await api.get(`/students/${effStudent}`)).data, enabled: !!effStudent });
  // The waiting room — expenses nobody has classified yet. Deliberately NOT
  // filtered by tax year, and deliberately not gated on a beneficiary being
  // selected: a room whose purpose is to empty must not be able to hide items
  // behind a filter, or the Overview says work is waiting and the tab shows
  // nothing. The parameters are built conditionally rather than interpolated —
  // `student_id=${undefined}` sends the literal text "undefined", which an
  // owner's request passes straight into the database filter, matching nothing.
  // (ZEN-113)
  const { data: draftList = [], isLoading: waitingLoading, isError: waitingError } = useQuery({
    queryKey: ["drafts-needing", effStudent || "all"],
    queryFn: async () => {
      const p = new URLSearchParams({ status: "draft" });
      if (effStudent) p.set("student_id", effStudent);
      return (await api.get(`/expenses?${p}`)).data;
    },
  });
  const { data: allStudents = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data });
  // THE WAITING ROOM COUNTS WHAT THE MENU COUNTS. (ZEN-687.) The marker beside
  // "Expenses" leaves out a beneficiary who has been archived — their
  // unfinished work used to add to it for ever with no way to clear it — and
  // this list is what that marker stands for, so it is built from the same
  // rule. Without this the two would disagree the moment a household archived
  // somebody, which is the exact fault that ticket exists to end.
  // An archived beneficiary you deliberately pick still shows theirs.
  const waitingList = forTheSelectedBeneficiary(
    draftList, effStudent, archivedBeneficiaryIds(allStudents));
  const waitingCount = waitingList.length;
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { data: parties } = useParties();
  const { data: psData } = usePaymentSources(true);
  const psSources = psData?.sources || [];
  const nickOf = (sid) => { const s = allStudents.find((x) => x.id === sid); return s?.nickname || s?.full_name?.split(" ")[0] || "Beneficiary"; };

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["expenses"] });
    qc.invalidateQueries({ queryKey: ["envelopes"] });
    qc.invalidateQueries({ queryKey: ["accounts-cards-summary"] });
    // Classifying an expense empties it out of the waiting room and lowers the
    // Overview's count. Without these two the record shows in a settled tab AND
    // still in the waiting room, with the navigation badge one too high, until
    // the cache goes stale on its own half a minute later. (ZEN-113)
    qc.invalidateQueries({ queryKey: ["drafts-needing"] });
    qc.invalidateQueries({ queryKey: ["attention"] });
  };
  const [singleDeleteId, setSingleDeleteId] = useState(null);
  const del = (id) => setSingleDeleteId(id);
  const doSingleDelete = async () => {
    const id = singleDeleteId; setSingleDeleteId(null);
    try { await api.delete(`/expenses/${id}`); toast.success("Deleted"); refresh(); } catch (e) { toast.error(apiErr(e)); }
  };
  const exportCsv = () => {
    if (!effStudent) { toast.error("Select a beneficiary to export"); return; }
    const params = { student_id: effStudent };
    if (year) params.tax_year = year;
    downloadExport("csv", params).catch((e) => toast.error(apiErr(e)));
  };
  const exportPdf = () => {
    if (!effStudent) { toast.error("Select a beneficiary to export"); return; }
    const params = { student_id: effStudent };
    if (year) params.tax_year = year;
    downloadExport("pdf", params).catch((e) => toast.error(apiErr(e)));
  };

  const isOwner = user.role === "owner";
  // Untied 529 money and the eye view's withdrawal dates. (ZEN-785)
  // The withdraw buttons below wait for this list (ZEN-791 review): until it
  // has loaded, nobody knows whether money already withdrawn covers part of
  // the need, and a slow or failed load must never offer the full amount.
  const { data: studentWithdrawals = [], isSuccess: withdrawalsKnown } = useStudentWithdrawals(effStudent);
  // Only the year on screen gets boxes; other years get one line. (ZEN-790)
  const { shown: untied, otherYears: untiedElsewhere } = isOwner
    ? untiedForYear(studentWithdrawals, year) : { shown: [], otherYears: [] };
  const seeUntiedYear = (y) => setYear(String(y));
  const tieUntied = (w) => nav("/withdrawals", { state: editWithdrawalNavState(w.id) });
  const todayISO = localISO();
  // Which tab each expense belongs to, and what each tab adds up to, lives in
  // lib/expenseTabs.js — it decides whether an expense reaches a total the owner
  // reads as fact, so it is one function with its own tests rather than four
  // filters inline. (ZEN-113)
  const {
    tab529, tabNon, tab529Total, tabNonTotal, tabExpenses, baseList, onWaitingTab,
    unpricedOnScreen,
  } = splitExpenseTabs({
    expenses, waiting: waitingList, activeTab, eligibleOnly, hideReconciled, missingReceiptOnly,
    needsRateOnly, onlyIds,
  });
  // AOTC reserved total — scoped to selected tax year + eligibility "yes"
  const { data: aotcReserveData } = useQuery({
    queryKey: ["aotc-reserve", effStudent, year],
    queryFn: async () => (await api.get(`/dashboard/aotc-reserve/${effStudent}?tax_year=${year}`)).data,
    enabled: !!effStudent && !!year,
  });
  const aotcEligYes = aotcReserveData?.aotc_eligibility === "yes";
  // THE SERVER'S TOTAL, NOT A COPY COMPUTED HERE. (ZEN-181)
  //
  // This used to reduce over the loaded rows with a paid-only filter, and it was
  // wrong two ways. First, a reservation on an expense recorded but not yet paid is
  // still spoken for — excluding it is the exact defect ZEN-181 fixes, and
  // leaving it here would have this page contradicting the Overview banner that
  // links to it. Second, it could never be authoritative anyway: GET /expenses
  // caps its result at 2,000 rows (routes/expenses.py), while the server
  // aggregate deliberately has no cap, so a large year would quietly under-count.
  //
  // The page already fetched the server's answer above and simply never used its
  // `marked` field. It does now.
  //
  // KNOWN AND ACCEPTED, NOT OVERLOOKED: this query is cached for 60 seconds with
  // focus-refetch off, and `refresh()` does not invalidate its key — so straight
  // after saving a reservation this figure can lag briefly. Invalidating all
  // three keys that hold it, and the cross-window case, is ZEN-222. Lagging by a
  // minute is strictly better than the previous state, which was wrong always.
  const aotcReservedTotal = (year && aotcEligYes) ? (aotcReserveData?.marked || 0) : 0;
  const showAotcSummary = !!year && aotcReservedTotal > 0;

  const sortVal = (e) => {
    switch (sortBy) {
      case "payee": return (e.payee || "").toLowerCase();
      case "category": return (e.category || "").toLowerCase();
      // SORTING, not a total: an unpriced row has no figure to order by and
      // sorts as nothing, which puts it at one end where it is easy to find.
      // Deliberately NOT the shared rule — this is not money being added up.
      case "usd": return e.usd_amount || 0;
      case "routing": return (expenseRoutingLabel(e, parties, nickOf(e.student_id)) || "").toLowerCase();
      case "tax_yr": return String(e.eligibility_tax_year || "");
      case "status": return STATUS_META[e.status]?.short || "";
      case "date":
      default: return e.transaction_date || "";
    }
  };
  const sortedExpenses = [...baseList].sort((a, b) => {
    const va = sortVal(a), vb = sortVal(b);
    const c = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
    return sortDir === "asc" ? c : -c;
  });
  // Show ORIGINAL column only when displayed expenses contain at least one non-USD currency
  const hasNonUSD = sortedExpenses.some((e) => !isUSD(curCode(e.original_currency, "USD")));
  const toggleSort = (k) => {
    if (sortBy === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(k); setSortDir("asc"); }
  };
  // Derive filter categories from expenses actually in use; fallback to all group categories
  const cats = (() => {
    const inUse = [...new Set(baseList.map((e) => e.category).filter(Boolean))].sort();
    if (inUse.length) return inUse;
    return ALL_IRS_CATEGORIES;
  })();
  const SortTh = ({ k, label, align, after }) => (
    <th className={`px-2 py-2 ${align === "right" ? "text-right" : ""}`}>
      <button onClick={() => toggleSort(k)} data-testid={`sort-${k}`}
        className={`inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-[#034AA2] ${align === "right" ? "flex-row-reverse" : ""} ${sortBy === k ? "text-[#034AA2]" : ""}`}>
        {label}
        {sortBy === k
          ? (sortDir === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />)
          : <ArrowsDownUp size={11} weight="bold" className="opacity-30" />}
      </button>
      {/* Room for a column's own help, OUTSIDE the sort button — inside it,
          every hover over the explainer would also read as "sort by this
          column". (ZEN-455) */}
      {after && <span className="ml-1 align-middle">{after}</span>}
    </th>
  );
  const visibleIds = sortedExpenses.map((e) => e.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));
  const toggleOne = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const toggleAll = () => setSelected(allSelected ? [] : visibleIds);
  const exitSelect = () => { setSelectMode(false); setSelected([]); };
  // Footer totals: all shown rows; then the active status (or Awaiting by default), to-date and through next month.
  const effStatus = status || "confirmed";
  const effStatusLabel = STATUS_META[effStatus]?.short || "Awaiting";
  const effStatusColor = STATUS_META[effStatus]?.bg || "#F01919";
  // EVERY MONEY TOTAL ON THIS PAGE WAS SWEPT, not just ones a review named:
  // tab529Total and tabNonTotal (lib/expenseTabs.js, spent-only), the AOTC
  // reserve above (paid-only), and totalAll here. totalAll KEEPS counting
  // scheduled rows, deliberately — its label counts every row on the list,
  // and a total that silently drops rows would contradict the count printed
  // beside it. What it must not do is let unpaid money pass as spend, so the
  // not-yet-paid part is named next to it. (ZEN-109)
  // EVERY TOTAL ON THIS PAGE ASKS THE ONE RULE, the same one the server's
  // figures ask. These four were written `|| 0`, which counts an expense
  // nobody could price as ZERO — so the Overview said "not counted in any
  // total" while this page counted it as nothing. Two screens, one sum of
  // money, two answers. (ZEN-632)
  const totalAll = pricedSum(baseList).total;
  // The shared two-part rule, not the status alone: the legacy allocated rows
  // the backfill preserves keep a spent status while future-dated, and their
  // unpaid money must be named here too. (Pre-merge review, round 3.)
  const totalScheduled = pricedSum(baseList.filter(isNotYetPaid)).total;
  const totalPaid = pricedSum(baseList.filter((e) => !isNotYetPaid(e))).total;
  const { data: homeEstExpenses } = useHomeEstimate(totalAll);
  // The two "Withdraw this amount" figures, and whether each may carry a
  // button, come from the one rule the withdrawal form also asks
  // (lib/withdrawThisAmount.js). (ZEN-582)
  const wta = useWithdrawThisAmount({
    rows: baseList, status: effStatus, studentId: effStudent,
    enabled: isOwner && activeTab === TAB_529,
  });
  const totalEffPast = wta.toDate.total;
  const totalEffNext = wta.inclNextMonth.total;
  // TIE FIRST, THEN WITHDRAW WHAT IS LEFT (ZEN-791, Maryse's option A, 21 Oct
  // 2026). The money this child already withdrew and tied to nothing, from the
  // tax year the window falls in, comes off what that same year's expenses
  // still need. Another tax year's need is never offered (rowsOfYear).
  const { throughISO: dueThrough, monthName: dueMonth } = comingUpWindow(localISO(new Date()));
  const dueYear = Number(dueThrough.slice(0, 4));
  const byIdHere = new Map(baseList.map((e) => [e.id, e]));
  // Incurred first, then what falls due (ZEN-796): the already-paid rows and
  // the not-yet-paid ones of that year go to the rule separately.
  const incurredRows = rowsOfYear(wta.toDate.rows, byIdHere, dueYear);
  const incurredIds = new Set(incurredRows.map((r) => r.id));
  const dueRows = rowsOfYear(wta.inclNextMonth.rows, byIdHere, dueYear).filter((r) => !incurredIds.has(r.id));
  const sumOf = (rows) => rows.reduce((t, r) => t + r.usd, 0);
  const tiePlan = isOwner && activeTab === TAB_529
    ? tieFirstPlan({ incurred: sumOf(incurredRows), due: sumOf(dueRows), withdrawals: studentWithdrawals, studentId: effStudent, year: dueYear }) : null;
  const leftPlan = tiePlan && !tiePlan.covers ? tiePlan : null;
  // This year's untied money hides the full-amount buttons even when nothing
  // of this year is due: those buttons would then offer only another tax
  // year's need, which her same-year ruling says is never offered (ZEN-791
  // review, round 4). The untied reminder still says to tie it first.
  const untiedThisYear = isOwner && activeTab === TAB_529
    && sameYearUntied(studentWithdrawals, effStudent, dueYear).length > 0;
  // The words come from lib/tieFirst.js; with nothing left to withdraw, no
  // withdraw link is offered.
  const reminderPlan = tiePlan && {
    ids: tiePlan.rows.map((w) => w.id),
    sentence: expensesReminderWords(tiePlan, dueRows.length, dueMonth, money),
    ...(leftPlan ? {
      withdrawLabel: `Withdraw ${money(leftPlan.left, "USD")} →`,
      onWithdraw: () => nav("/withdrawals", { state: withdrawLeftNavState(leftPlan, dueMonth) }),
    } : {}),
  };
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [delBusy, setDelBusy] = useState(false);

  const doDeleteSelected = async () => {
    setDeleteConfirm(false); setDelBusy(true);
    try {
      const { data } = await api.delete("/expenses/bulk", { data: { ids: selected } });
      toast.success(`Deleted ${data.deleted} expense${data.deleted === 1 ? "" : "s"}`);
      exitSelect(); refresh();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setDelBusy(false); }
  };

  return (
    <div>
      <PageHeader
        title={<>Expenses{/* THE PHONE'S COPY OF THE PAPERCLIP HELP. The table
          — and with it the column-header explainer — only exists from 640px
          up, so on a phone this is the only place it can live. Exactly one is
          ever on screen. (ZEN-455) */}
          <span className="ml-1.5 align-middle sm:hidden">
            <AttachmentHelp withGapLine testid="exp-paperclip-help-mobile" />
          </span>
        </>}
        subtitle={effStudent ? "" : "Showing all beneficiaries.\nSelect a beneficiary to add, edit, or export expense(s)."}
        actions={
          <div className="flex flex-col items-end gap-2">
            <div className="flex flex-wrap items-center justify-end gap-2">
              {!attentionKey && <>
              <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="filter-year" className="h-9 rounded-md border border-[#C2C9D1] bg-white px-2 text-sm font-semibold">
                <option value="">All tax years</option>
                {/* The year on screen is always among the choices, even an old one. */}
                {[...new Set([...taxYears(), ...(year ? [Number(year)] : [])])].sort((a, b) => b - a)
                  .map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <StatusFilter value={status} onChange={setStatus} />
              </>}
              <button onClick={() => { setEligibleOnly((v) => { if (!v) setHideReconciled(false); return !v; }); }} data-testid="filter-eligible-chip"
                className={`flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-bold transition-colors ${eligibleOnly ? "bg-[#077A39] text-white" : "border border-[#077A39] text-[#077A39] hover:bg-[#077A39]/5"} ${activeTab !== "529" || attentionKey ? "hidden" : ""}`}>
                <Funnel size={14} weight="bold" /> 529-eligible to withdraw
              </button>
              <button onClick={() => { setHideReconciled((v) => { if (!v) setEligibleOnly(false); return !v; }); }} data-testid="filter-hide-reconciled"
                className={`flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-bold transition-colors ${hideReconciled ? "bg-[#034AA2] text-white" : "border border-[#034AA2] text-[#034AA2] hover:bg-[#034AA2]/5"} ${attentionKey ? "hidden" : ""}`}>
                <EyeSlash size={14} weight="bold" /> {hideReconciled ? "Unhide reconciled/closed" : "Hide reconciled/closed"}
              </button>
              {missingReceiptOnly && !attentionKey && (
                <button onClick={() => setMissingReceiptOnly(false)} data-testid="filter-missing-receipt-chip"
                  className="flex h-9 items-center gap-1.5 rounded-full bg-[#D97706] px-3 text-xs font-bold text-white transition-colors hover:bg-[#B45309]">
                  <Receipt size={14} weight="bold" /> Missing receipts only <X size={12} weight="bold" />
                </button>
              )}
              {/* A TOTAL THAT IS SHORT SAYS SO, HERE TOO. The figures on this page
                  leave out what nobody could price, exactly as the Overview's do,
                  and a reader cannot tell a short total from a complete one. One
                  click turns the filter on and shows them. Hidden while the
                  filter is already on, because the chip beside it says the same
                  thing. (ZEN-632) */}
              {!needsRateOnly && unpricedOnScreen > 0 && (
                <button onClick={() => setNeedsRateOnly(true)} data-testid="totals-unpriced-note"
                  className="flex h-9 items-center gap-1.5 rounded-full border border-[#D97706] px-3 text-xs font-bold text-[#B45309] transition-colors hover:bg-[#FFFBEB]">
                  <Warning size={14} weight="bold" />
                  {notInTheseTotals(unpricedOnScreen)}
                </button>
              )}
              {needsRateOnly && (
                <button onClick={() => setNeedsRateOnly(false)} data-testid="filter-needs-rate-chip"
                  className="flex h-9 items-center gap-1.5 rounded-full bg-[#D97706] px-3 text-xs font-bold text-white transition-colors hover:bg-[#B45309]">
                  <Warning size={14} weight="bold" /> {NO_DOLLAR_AMOUNT_FILTER} <X size={12} weight="bold" />
                </button>
              )}
              {attentionKey && (
                <button onClick={() => { setAttentionKey(null); setAttentionIds(null); }} data-testid="filter-attention-chip"
                  className="flex h-9 items-center gap-1.5 rounded-full bg-[#D97706] px-3 text-xs font-bold text-white transition-colors hover:bg-[#B45309]">
                  <Warning size={14} weight="bold" /> {ATTENTION_ONLY_CHIP} <X size={12} weight="bold" />
                </button>
              )}
              {effStudent && <Btn variant="ghost" onClick={exportCsv} data-testid="export-csv-btn"><DownloadSimple size={16} weight="bold" /> CSV</Btn>}
              {effStudent && <Btn variant="ghost" onClick={exportPdf} data-testid="export-pdf-btn"><FilePdf size={16} weight="bold" /> PDF</Btn>}
            </div>
            {isOwner && (
              <div className="flex flex-col items-end" style={{ gap: "10px" }}>
                <div className="flex items-stretch" style={{ gap: "10px" }}>
                  <button
                    onClick={() => { if (!effStudent) { toast.error("Select a beneficiary first"); return; } setEditing(null); setDlg(true); }}
                    data-testid="add-expense-btn"
                    className="inline-flex h-9 items-center justify-center rounded-lg px-4 transition-colors hover:brightness-90"
                    style={{ fontSize: "15px", fontWeight: 500, gap: "8px", background: "#185FA5", color: "#fff", border: "1.5px solid #185FA5" }}
                  ><Plus size={18} weight="bold" /> Add expense</button>
                </div>
                {/* Also offered when only the waiting room has anything in it —
                    editing several at once is a natural way to clear it, and
                    gating on `expenses.length` alone hid the control behind a
                    tax-year filter. (ZEN-113) */}
                {effStudent && (expenses.length > 0 || waitingCount > 0) && (
                  selectMode
                    ? <button onClick={exitSelect} data-testid="exit-select-btn"
                        className="inline-flex h-9 w-full items-center justify-center rounded-lg px-4 transition-colors hover:bg-[#E9E9EB]"
                        style={{ fontSize: "15px", fontWeight: 400, gap: "8px", background: "#fff", color: "#6B7280", border: "0.5px solid #E3E3E0" }}
                      ><X size={18} weight="bold" /> Done selecting</button>
                    : <button onClick={() => setSelectMode(true)} data-testid="delete-multiple-btn"
                        className="inline-flex h-9 w-full items-center justify-center rounded-lg px-4 transition-colors hover:bg-[#E9E9EB]"
                        style={{ fontSize: "15px", fontWeight: 400, gap: "8px", background: "#fff", color: "#6B7280", border: "0.5px solid #E3E3E0" }}
                      ><Trash size={18} weight="bold" /> Delete multiple expenses</button>
                )}
              </div>
            )}
          </div>
        }
      />
      <PageHelpPanel pageKey="expenses" hint={activeTab === TAB_NON529
        ? "Non-529 expenses are family costs you track outside the 529 — for your own records or settling up between family members. They never touch your 529 caps or tax reports."
        : undefined} />

      {/* ── Eligibility tabs ───────────────────────────── */}
      {/* The strip renders when there is anything in EITHER the settled list or
          the waiting room. Gating it on `expenses.length` alone hid the whole
          strip — and with it the only route to the waiting room — whenever a tax
          year was selected in which every expense happened to be settled.
          The three buttons carry a real flex basis rather than `flex-1`
          (`flex: 1 1 0%`), because a zero basis cannot wrap: the tabs would
          shrink instead, and the "To classify" tab does not fit beside two others
          on a phone. (ZEN-113) */}
      {(expenses.length > 0 || waitingCount > 0 || activeTab === TAB_NEEDS_DECISION) && (
        <div className="mb-4 flex flex-wrap gap-y-0.5 rounded-lg border border-[#C2C9D1] bg-[#E9E9EB]/60 p-0.5" data-testid="expense-tabs">
          <button
            onClick={() => { setActiveTab(TAB_529); setEligibleOnly(false); }}
            data-testid="tab-529"
            className={`flex grow basis-[calc(50%-2px)] items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold transition-all sm:basis-0 ${activeTab === TAB_529 ? "bg-white text-[#077A39] shadow-sm" : "text-[#6b7280] hover:text-[#374151]"}`}
          >
            <span>529-eligible</span>
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none ${activeTab === TAB_529 ? "bg-[#077A39]/10 text-[#077A39]" : "bg-[#C2C9D1]/60 text-[#6b7280]"}`}>{tab529.length}</span>
            <span className={`hidden text-xs sm:inline ${activeTab === TAB_529 ? "text-[#077A39]/70" : "text-[#9aa1a9]"}`}>{money(tab529Total, "USD")}</span>
          </button>
          <button
            onClick={() => { setActiveTab(TAB_NON529); setEligibleOnly(false); }}
            data-testid="tab-non529"
            className={`flex grow basis-[calc(50%-2px)] items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold transition-all sm:basis-0 ${activeTab === TAB_NON529 ? "bg-white text-[#475569] shadow-sm" : "text-[#6b7280] hover:text-[#374151]"}`}
          >
            <span>Non-529</span>
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none ${activeTab === TAB_NON529 ? "bg-[#475569]/10 text-[#475569]" : "bg-[#C2C9D1]/60 text-[#6b7280]"}`}>{tabNon.length}</span>
            <span className={`hidden text-xs sm:inline ${activeTab === TAB_NON529 ? "text-[#475569]/70" : "text-[#9aa1a9]"}`}>{money(tabNonTotal, "USD")}</span>
          </button>
          {/* Shown while the room has anything in it, AND while the owner is
              standing in it — otherwise classifying the last expense deletes the
              tab out from under them, leaving nothing selected. No dollar total:
              a figure on money nobody has classified invites being read as a
              number that means something. */}
          {(waitingCount > 0 || activeTab === TAB_NEEDS_DECISION) && (
            <button
              onClick={() => { setActiveTab(TAB_NEEDS_DECISION); setEligibleOnly(false); setMissingReceiptOnly(false); }}
              data-testid="tab-needs-decision"
              className={`flex grow basis-[calc(50%-2px)] items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold transition-all sm:basis-0 ${activeTab === TAB_NEEDS_DECISION ? "bg-white text-[#B45309] shadow-sm" : "text-[#6b7280] hover:text-[#374151]"}`}
            >
              <span>{STATUS_META.draft.label}</span>
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none ${activeTab === TAB_NEEDS_DECISION ? "bg-[#D97706]/10 text-[#B45309]" : "bg-[#C2C9D1]/60 text-[#6b7280]"}`}>{waitingCount}</span>
            </button>
          )}
        </div>
      )}

      <StatusLegend />

      {/* The amber "N drafts need a 529-eligibility decision" banner that used to
          sit here is now the "To classify" tab above. One control for one
          job: the banner only appeared once the owner was already on this page
          AND had a beneficiary selected, and it disappeared the moment it was
          clicked — it stopped reminding them at exactly the point they started
          work. The tab does not, and the Overview carries the amber. (ZEN-113) */}
      {onWaitingTab && !waitingError && (
        <p className="mb-3 text-xs text-[#6b7280]" data-testid="waiting-room-year-note">
          This tab ignores the tax-year filter above, so nothing waiting on you can be hidden.
        </p>
      )}

      {/* On EVERY tab, not just inside the room. A failed request leaves the
          count at zero, which hides the tab entirely — so the owner would never
          reach a message that only appeared once they were in there. Silence
          would read as "nothing is waiting", which is the one thing this page
          must never say when it does not know. (ZEN-113) */}
      {waitingError && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-[#B45309]/30 bg-[#FEF3C7] px-4 py-2.5 text-sm text-[#92400E]" data-testid="waiting-room-error">
          <Warning size={18} weight="fill" className="shrink-0 text-[#B45309]" />
          <span>Couldn&apos;t check whether anything is waiting for a decision. Refresh the page to try again.</span>
        </div>
      )}

      {selectMode && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-[#034AA2]/30 bg-[#034AA2]/5 px-4 py-2.5 text-sm text-[#034AA2]" data-testid="select-mode-banner">
          <ListChecks size={18} weight="bold" />
          <span className="font-semibold">Tick the expenses you want to edit</span>
          <span className="text-[#6b7280]">— then choose a field to change for all of them at once.</span>
        </div>
      )}

      {/* `waitingLoading` is in this test on purpose. The waiting room comes from
          its OWN request, and drafts are stripped out of the main list — so
          while that second request is still in flight, `waitingCount` is zero
          and the page would confidently announce "Nothing is waiting" to an
          owner who has work waiting. A room that can report itself empty while
          it is full is worse than no room. (ZEN-113) */}
      {(!yearDecided || isLoading || waitingLoading || (attentionKey && attentionIds === null)) ? (
        <div className="h-64 animate-pulse rounded-xl border border-[#C2C9D1] bg-white" />
      ) : (expenses.length === 0 && waitingCount === 0 && !onWaitingTab && !waitingError) ? (
        /* `!onWaitingTab` matters: classify the last waiting expense while a tax
           year is selected that excludes the result, and BOTH counts hit zero.
           Without it the owner gets "No expenses yet" — as if their records had
           vanished — instead of the green "nothing is waiting" they just earned. */
        /* A year with nothing in it says so by name; "yet" is only true when
           every year is on screen. (ZEN-762, her wording 2026-10-12.) */
        <EmptyState icon={CurrencyDollar} title={year ? `No expenses for ${year}` : "No expenses yet"}
          subtitle={year ? "Try selecting a different tax year or 'All tax years'."
            : isOwner ? "Add your first expense or upload a receipt to auto-fill one." : "No expenses recorded for you yet."}
          action={isOwner ? <Btn onClick={() => setDlg(true)}><Plus size={16} weight="bold" /> Add expense</Btn> : null} />
      ) : tabExpenses.length === 0 ? (
        /* The room just emptied under the owner — that is the good outcome, so it
           reads green rather than as an absence. The two settled tabs no longer
           claim "everything is in the other tab", which stopped being true the
           moment there were three of them. (ZEN-113) */
        onWaitingTab ? (
          waitingError ? (
            <EmptyState icon={Warning} title="Couldn't check what's waiting"
              subtitle="This list didn't load, so it may not be empty. Refresh the page to try again."
              iconClassName="mb-3 text-[#B45309]" />
          ) : (
          <EmptyState icon={CheckCircle} title="Nothing is waiting for a decision"
            subtitle="Every expense is now filed under 529-eligible or Non-529."
            iconClassName="mb-3 text-[#077A39]" />
          )
        ) : (
          <EmptyState icon={CurrencyDollar}
            title={activeTab === TAB_529 ? "No 529-eligible expenses" : "No non-529 expenses"}
            subtitle={activeTab === TAB_529 ? "Nothing has been filed as 529-eligible yet." : "Nothing has been filed as non-529 yet."} />
        )
      ) : (
        <>
        {attentionKey === "overfunded_expense" && (
          <OverfundedExplanation attention={attention} expenses={expenses} withdrawals={studentWithdrawals} />
        )}
        {/* AOTC reserved summary — 529 tab only, scoped to selected year + eligibility "yes" */}
        {activeTab === "529" && showAotcSummary && (
          <div className="mb-3 flex items-center gap-2" data-testid="aotc-reserved-summary">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0369A1] px-2.5 py-1 text-[11px] font-bold text-white">AOTC</span>
            <span className="text-xs font-semibold text-[#0369A1]">{money(aotcReservedTotal, "USD")} reserved for AOTC</span>
            <InfoTip testid="aotc-summary-info" width="w-72">{aotcTotalInfo(year)}</InfoTip>
          </div>
        )}
        <Card className="hidden overflow-x-auto p-0 sm:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#C2C9D1] text-left text-xs uppercase tracking-wide text-[#1f2937]">
                {selectMode && <th className="px-2 py-2"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all-checkbox" className="h-4 w-4 cursor-pointer accent-[#034AA2]" /></th>}
                <th className="px-2 py-2">
                  <div className="flex flex-col">
                    <button onClick={() => toggleSort("date")} data-testid="sort-date"
                      className={`inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-[#034AA2] ${sortBy === "date" ? "text-[#034AA2]" : ""}`}>
                      Date
                      {sortBy === "date"
                        ? (sortDir === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />)
                        : <ArrowsDownUp size={11} weight="bold" className="opacity-30" />}
                    </button>
                    <button onClick={() => toggleSort("tax_yr")} data-testid="sort-tax_yr"
                      className={`inline-flex items-center gap-1 text-[10px] tracking-wide transition-colors hover:text-[#034AA2] ${sortBy === "tax_yr" ? "text-[#034AA2] font-bold" : "text-[#6b7280]"}`}>
                      TY
                      {sortBy === "tax_yr"
                        ? (sortDir === "asc" ? <CaretUp size={9} weight="bold" /> : <CaretDown size={9} weight="bold" />)
                        : <ArrowsDownUp size={9} weight="bold" className="opacity-30" />}
                    </button>
                  </div>
                </th>
                <SortTh k="payee" label="Payee"
                  after={<AttachmentHelp withGapLine testid="exp-paperclip-help" />} />
                <SortTh k="category" label="Category" />
                {hasNonUSD && <th className="hidden px-2 py-2 text-right lg:table-cell" data-testid="col-original">Original</th>}
                <SortTh k="usd" label="USD" align="right" />
                <th className="hidden px-2 py-2 lg:table-cell"><button onClick={() => toggleSort("routing")} data-testid="sort-routing" className={`inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-[#034AA2] ${sortBy === "routing" ? "text-[#034AA2]" : ""}`}>Routing{sortBy === "routing" ? (sortDir === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />) : <ArrowsDownUp size={11} weight="bold" className="opacity-30" />}</button></th>
                <SortTh k="status" label="Status" />
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {sortedExpenses.map((e) => {
                const future = (e.transaction_date || "") > todayISO;
                const atts = expenseAttachments(e, receiptIndex);
                // TWO LAYOUTS ARE IN THE PAGE AT ONCE — the table and the card
                // list — and only one is on screen at a time. They must not
                // share a panel key, because the key becomes the panel's id in
                // the page and two elements answering to one id is what makes
                // "this button opens that panel" ambiguous for a screen reader.
                const k = `${e.id}-table`;
                return (
                <React.Fragment key={e.id}>
                <tr className={`border-t border-[#C2C9D1] hover:bg-[#E9E9EB]/50 ${future ? "bg-[#F8FAFC] opacity-55" : ""} ${selected.includes(e.id) ? "bg-[#034AA2]/5" : ""}`} data-testid={`expense-row-${e.id}`} title={future ? "Future-dated — has not occurred yet" : undefined}>
                  {selectMode && <td className="px-2 py-2"><input type="checkbox" checked={selected.includes(e.id)} onChange={() => toggleOne(e.id)} data-testid={`select-expense-${e.id}`} className="h-4 w-4 cursor-pointer accent-[#034AA2]" /></td>}
                  <td className="px-2 py-2 whitespace-nowrap tnum">
                    <div>{fmtDate(e.transaction_date)}{future && <span className="ml-1.5 rounded-full bg-[#E9E9EB] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#6b7280]">future</span>}</div>
                    {/* "TY" reads as a claim about where the money is reported.
                        "Counts toward" says what it actually is. (ZEN-172) */}
                    <div className="text-[10px] text-[#9aa1a9]" data-testid={`exp-tax-year-${e.id}`}>Counts toward {e.eligibility_tax_year}</div>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold">{e.payee}</span>
                      {/* The paperclip IS the button now. It used to say a file
                          existed and do nothing when clicked, which is the
                          instinct everybody has; this makes the instinct
                          correct. No attachment, no paperclip, no way in —
                          one symbol, one meaning. (ZEN-351) */}
                      <AttachmentButton
                        ref={attachPanel.buttonRef(k)}
                        variant="dense"
                        attachments={atts}
                        recordLabel={expenseLabel(e)}
                        expanded={attachPanel.isOpen(k)}
                        panelId={attachPanel.panelId(k)}
                        onToggle={() => attachPanel.toggle(k)}
                        testid={`exp-receipt-icon-${k}`}
                      />
                      {/* Only when this expense lists a receipt that is not in
                          the records. An empty row says nothing. (ZEN-455) */}
                      <AttachmentGap
                        unavailableCount={e.attachments_unavailable}
                        recordLabel={expenseLabel(e)}
                        testid={`exp-attach-status-${k}`}
                      />
                    </div>
                    {e.description && <div className="text-xs text-[#6b7280]">{e.description}</div>}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex flex-wrap items-center gap-1">
                      {e.category && <span className="inline-block rounded-full px-2 py-0.5 text-xs font-semibold" style={{ background: "#E7E7E3", color: "#48473F" }}>{e.category}</span>}
                      {e.aotc_reserved && (
                        <span className="inline-block rounded-full bg-[#0369A1] px-1.5 py-0.5 text-[9px] font-bold uppercase text-white" title={AOTC_ROW_TIP} data-testid={`aotc-chip-${e.id}`}>AOTC</span>
                      )}
                    </div>
                  </td>
                  {hasNonUSD && (
                  <td className="hidden px-2 py-2 text-right tnum whitespace-nowrap lg:table-cell">{money(e.original_amount, e.original_currency)}
                    {!isUSD(e.original_currency) && (
                      <div className="text-[10px] text-[#6b7280] flex items-center justify-end gap-1">
                        @ {e.fx_rate || (e.usd_amount && e.original_amount ? (e.usd_amount / e.original_amount).toFixed(4) : "—")}
                        {e.fx_revision_log_legacy?.length > 0 && !e.fx_review_ack && (
                          <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700 leading-none" data-testid={`exp-fx-corrected-${e.id}`}>FX corrected</span>
                        )}
                      </div>
                    )}
                  </td>
                  )}
                  <td className="px-2 py-2 text-right tnum font-bold">
                    {money(e.usd_amount, "USD")}
                    {e.is_fx_estimate && !needsAManualRate(e) && (
                      <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700 leading-none" data-testid={`exp-fx-estimate-${e.id}`}>est.</span>
                    )}
                    {/* The amount cell already shows a dash rather than a made-up
                        $0.00, which is honest but silent — it does not say WHY
                        there is no figure or what would fix it. The Overview
                        counts these and sends the owner here; this is what they
                        are meant to find when they arrive. (ZEN-632) */}
                    {/* needsAManualRate, NOT the raw field — the filter that
                        brought the owner here uses that test, and a badge
                        asking a narrower question would show them a row with no
                        marker on it, having just promised to point at it. One
                        test, three places: the count, the filter, the badge.
                        (Adversarial review, round 9.) */}
                    {/* TWO DIFFERENT SITUATIONS, TWO BADGES (ZEN-555). An expense
                        with no dollar amount is either WAITING for a rate that
                        will arrive by itself, or NEEDS one typed because no
                        source publishes its currency. Telling the first family
                        to "enter a rate" is the request Maryse ruled out. Both
                        still pass the one test above, so the count, the filter
                        and a badge still agree row for row. */}
                    {needsAManualRate(e) && (isAwaitingARate(e, true) ? (
                      <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700 leading-none"
                        title={AWAITING_A_RATE_HOVER}
                        data-testid={`exp-awaiting-rate-${e.id}`}>{AWAITING_A_RATE_BADGE}</span>
                    ) : (
                      <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700 leading-none"
                        title={NEEDS_A_RATE_HOVER}
                        data-testid={`exp-needs-rate-${e.id}`}>{NEEDS_A_RATE_BADGE}</span>
                    ))}
                    {e.alloc_usd > 0.005 && e.status === "confirmed" && (() => {
                      const allocs = e.allocations || [];
                      return (
                        <div className="mt-0.5 space-y-0.5 text-[11px] font-semibold leading-tight" data-testid={`exp-partial-alloc-${e.id}`}>
                          {allocs.map((a, i) => (
                            <div key={i} className="text-[#077A39]">
                              {money(a.amount_usd, "USD")} Reconciled
                              {a.fx_rate && !isUSD(e.original_currency) && (
                                <span className="ml-1 font-normal text-[#077A39]/70">@ {a.fx_rate}</span>
                              )}
                            </div>
                          ))}
                          {Math.max(0, (e.usd_amount || 0) - e.alloc_usd) > 0.005 && (
                            <div className="text-[#F01919]">
                              {money(Math.max(0, (e.usd_amount || 0) - e.alloc_usd), "USD")} Awaiting
                              {!isUSD(e.original_currency) && (
                                <span className="ml-1 font-normal text-[#F01919]/70">@ {e.fx_rate || (e.usd_amount && e.original_amount ? (e.usd_amount / e.original_amount).toFixed(4) : "—")}</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="hidden px-2 py-2 text-xs text-[#6b7280] lg:table-cell">
                    {expenseRoutingLabel(e, parties, nickOf(e.student_id))}
                    {e.payment_source_id && psById(psSources, e.payment_source_id) && (
                      <div className="text-[10px] text-[#9aa1a9]">funded via {psLabel(psById(psSources, e.payment_source_id), psOwnerName(psById(psSources, e.payment_source_id), allStudents, parties))}</div>
                    )}
                  </td>
                  <td className="px-2 py-2"><StatusChip status={e.status} expense={e} /></td>
                  <td className="px-2 py-2">
                    <div className="flex items-center justify-end gap-1">
                      {/* An expense waiting for a decision gets a named action, not
                          a pencil. The work the owner has to do here is answer a
                          question, and "edit" does not say that. (ZEN-114) */}
                      {/* The eye: a way to READ this expense, before the pencil and shown to
                          everyone who can see the row — a beneficiary, who cannot edit, has
                          no other way in. (ZEN-702) */}
                      <ViewButton onClick={() => setViewing(e)} testid={`view-expense-${e.id}`} />
                      {isOwner && (e.status === "draft" ? (
                        <button onClick={() => { setEditing(e); setDlg(true); }} data-testid={`classify-expense-${e.id}`}
                          className="rounded-md bg-[#034AA2] px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#023B82]">Classify</button>
                      ) : (
                        <button onClick={() => { setEditing(e); setDlg(true); }} data-testid={`edit-expense-${e.id}`} className="rounded p-1.5 text-[#034AA2] hover:bg-[#E9E9EB]"><PencilSimple size={16} weight="bold" /></button>
                      ))}
                      {isOwner && !e.is_split_parent && (
                        <button onClick={() => setSplitTarget(e)} data-testid={`split-expense-${e.id}`} className="rounded p-1.5 text-[#6b7280] hover:bg-[#E9E9EB]" title="Split expense"><ListChecks size={16} weight="bold" /></button>
                      )}
                      {isOwner && (
                        <button onClick={() => del(e.id)} data-testid={`delete-expense-${e.id}`} className="rounded p-1.5 text-[#F01919] hover:bg-[#F01919]/10"><Trash size={16} weight="bold" /></button>
                      )}
                    </div>
                  </td>
                </tr>
                {/* A row of its own, directly under the expense, spanning the
                    whole table. The expense above it never moves and is never
                    covered — which is the entire requirement. */}
                {attachPanel.isOpen(k) && (
                  <tr data-testid={`expense-attach-row-${k}`}>
                    <td colSpan={(selectMode ? 1 : 0) + (hasNonUSD ? 8 : 7)} className="px-2 pb-2">
                      <AttachmentPanel
                        id={attachPanel.panelId(k)}
                        attachments={atts}
                        recordLabel={expenseLabel(e)}
                        onClose={() => attachPanel.close(k)}
                        testid={`exp-attach-panel-${k}`}
                      />
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={(selectMode ? 1 : 0) + (hasNonUSD ? 8 : 7)} className="p-0">
                  <div className="border-t-2 border-[#C2C9D1] bg-[#F0F2F5] px-4 py-2.5 space-y-1.5" data-testid="expenses-footer">
                    {/* No totals in the waiting room. Putting a dollar figure on
                        money nobody has classified invites reading it as a number
                        that means something — and it would be the one total on
                        this page that belongs in no report. The tab above carries
                        a count and no amount for the same reason. (ZEN-113) */}
                    {onWaitingTab ? (
                      <div className="text-xs text-[#6b7280]" data-testid="waiting-room-no-total">
                        {baseList.length} waiting · no total, because none of these has been classified yet
                      </div>
                    ) : (<>
                    {/* Row 1 — grand total */}
                    <div className="flex items-center gap-1.5" data-testid="total-all">
                      <span className="text-xs font-semibold text-[#374151]">{eligibleOnly ? `529-eligible to withdraw (${baseList.length})` : hideReconciled ? `Reconciled hidden (${baseList.length})` : status ? `${effStatusLabel} — total (${baseList.length})` : listedLabel(baseList.length)}</span>
                      {(eligibleOnly || status) && <span className="text-xs text-[#9aa1a9]">{eligibleOnly ? "529-eligible, not yet withdrawn" : "current status filter"}</span>}
                      <span className="text-xs text-[#C2C9D1]">·</span>
                      <span className="tnum text-xs font-bold text-[#1f2937]">{money(totalAll, "USD")}</span>
                      <HomeCurrencyEstimate estimate={homeEstExpenses?.estimate} />
                    </div>
                    {totalScheduled > 0.005 && (
                      <div className="flex items-center gap-1.5 pl-3" data-testid="exp-total-scheduled-desktop">
                        <span className="text-xs text-[#9aa1a9]">Paid ·</span>
                        <span className="tnum text-xs font-semibold text-[#1f2937]">{money(totalPaid, "USD")}</span>
                        <span className="ml-3 text-xs text-[#9aa1a9]">Not paid yet ·</span>
                        <span className="tnum text-xs font-semibold text-[#B45309]">{money(totalScheduled, "USD")}</span>
                      </div>
                    )}
                    {/* Rows 2 and 3 are 529 withdrawal math — what is owed to you out
                        of the plan, and a shortcut to go withdraw it. Non-529 expenses
                        are settled privately between family members and never touch the
                        529, so on that tab these rows are not merely irrelevant, they
                        are wrong: they invite a withdrawal against expenses that can
                        never justify one. The grand total above stays. (Owner report,
                        2026-08-09; see the non-529 wall — these never enter 529 caps or
                        tax reports.) */}
                    {activeTab === TAB_529 && allYears && <AllYearsNote testid="all-years-note-desktop" />}
                    {activeTab === TAB_529 && !allYears && (<>
                      {/* Row 2 — awaiting to date */}
                      <div className="flex items-center gap-1.5" data-testid="total-awaiting-past">
                        <span className="text-xs font-semibold" style={{ color: effStatusColor }}>{effStatusLabel} · to date</span>
                        <span className="text-xs text-[#9aa1a9]">on or before today</span>
                        <span className="text-xs text-[#C2C9D1]">·</span>
                        <span className="tnum text-xs font-bold" style={{ color: effStatusColor }}>{money(totalEffPast, "USD")}</span>
                        {isOwner && wta.toDateButton && !tiePlan && !untiedThisYear && withdrawalsKnown && (
                          <button onClick={() => nav("/withdrawals", { state: withdrawNavState(wta.toDate) })} data-testid="withdraw-this-amount-to-date" className="ml-1 rounded-full bg-[#077A39] px-2.5 py-0.5 text-[11px] font-bold text-white hover:bg-[#066330] transition-colors">Withdraw this amount →</button>
                        )}
                      </div>
                      {/* Row 3 — awaiting incl. next month + withdraw button */}
                      <div className="flex items-center gap-1.5" data-testid="total-awaiting-next">
                        <span className="text-xs font-semibold" style={{ color: effStatusColor }}>{effStatusLabel} · incl. next month</span>
                        <span className="text-xs text-[#9aa1a9]" data-testid="total-awaiting-next-note">{wta.addsNextMonth ? NEXT_MONTH_NOTE : DECEMBER_NOTE}</span>
                        <span className="text-xs text-[#C2C9D1]">·</span>
                        <span className="tnum text-xs font-bold" style={{ color: effStatusColor }}>{money(totalEffNext, "USD")}</span>
                        {isOwner && wta.inclNextMonthButton && !tiePlan && !untiedThisYear && withdrawalsKnown && (
                          <button onClick={() => nav("/withdrawals", { state: withdrawNavState(wta.inclNextMonth) })} data-testid="withdraw-this-amount" className="ml-1 rounded-full bg-[#077A39] px-2.5 py-0.5 text-[11px] font-bold text-white hover:bg-[#066330] transition-colors">Withdraw this amount →</button>
                        )}
                      </div>
                    </>)}
                    {activeTab === TAB_529 && <div className="pt-1"><UntiedWithdrawalReminder withdrawals={untied} onTie={tieUntied} otherYears={untiedElsewhere} onSeeYear={seeUntiedYear} plan={reminderPlan} testid="untied-reminder-desktop" /></div>}
                    </>)}
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </Card>

        {/* Mobile expense cards — below sm only */}
        <div className="sm:hidden">
          {/* Mobile sort control */}
          <div className="mb-3 flex items-center gap-2">
            <span className="text-xs font-semibold text-[#6b7280]">Sort:</span>
            <select
              value={sortBy}
              onChange={(ev) => { setSortBy(ev.target.value); }}
              data-testid="mobile-sort-select"
              className="h-8 rounded-md border border-[#C2C9D1] bg-white px-2 text-xs font-medium"
            >
              <option value="date">Date</option>
              <option value="tax_yr">Tax Year</option>
              <option value="payee">Payee</option>
              <option value="category">Category</option>
              <option value="usd">USD Amount</option>
              <option value="routing">Routing</option>
              <option value="status">Status</option>
            </select>
            <button onClick={() => setSortDir((d) => d === "asc" ? "desc" : "asc")} className="flex h-8 w-8 items-center justify-center rounded-md border border-[#C2C9D1] bg-white text-[#6b7280]">
              {sortDir === "asc" ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
            </button>
          </div>
          <div className="space-y-3">
            {sortedExpenses.map((e) => {
              const future = (e.transaction_date || "") > todayISO;
              const atts = expenseAttachments(e, receiptIndex);
              const k = `${e.id}-card`;   // see the note on the table above
              return (
                <Card key={e.id} className={`p-3 ${future ? "opacity-55" : ""} ${selected.includes(e.id) ? "border-[#034AA2] bg-[#034AA2]/5" : ""}`} data-testid={`expense-row-${e.id}`}>
                  <div className="flex items-start gap-2">
                    {selectMode && <input type="checkbox" checked={selected.includes(e.id)} onChange={() => toggleOne(e.id)} data-testid={`select-expense-${e.id}`} className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-[#034AA2]" />}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold">{e.payee}</span>
                            {/* Full 44-pixel target here: on a phone this is the
                                only way in, and a paperclip sized as decoration
                                is smaller than a finger. (ZEN-351) */}
                            <AttachmentButton
                              ref={attachPanel.buttonRef(k)}
                              attachments={atts}
                              recordLabel={expenseLabel(e)}
                              expanded={attachPanel.isOpen(k)}
                              panelId={attachPanel.panelId(k)}
                              onToggle={() => attachPanel.toggle(k)}
                              testid={`exp-receipt-icon-${k}`}
                            />
                            {/* Same note as the table row. (ZEN-455) */}
                            <AttachmentGap
                              unavailableCount={e.attachments_unavailable}
                              recordLabel={expenseLabel(e)}
                              testid={`exp-attach-status-card-${k}`}
                            />
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <span className="tnum text-sm font-bold">{money(e.usd_amount, "USD")}{e.is_fx_estimate && <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700 leading-none">est.</span>}</span>
                          {e.original_currency && !isUSD(e.original_currency) && (
                            <div className="tnum text-[11px] text-[#6b7280]">{money(e.original_amount, e.original_currency)}</div>
                          )}
                        </div>
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-2">
                        <span className="text-xs text-[#6b7280] tnum">{fmtDate(e.transaction_date)} · counts toward {e.eligibility_tax_year}{future ? " · future" : ""}</span>
                        <StatusChip status={e.status} expense={e} />
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                        {e.category && <span className="inline-block rounded-full px-2 py-0.5 font-semibold" style={{ background: "#E7E7E3", color: "#48473F" }}>{e.category}</span>}
                        {e.aotc_reserved && (
                          <span className="inline-block rounded-full bg-[#0369A1] px-1.5 py-0.5 text-[9px] font-bold uppercase text-white" title={AOTC_ROW_TIP} data-testid={`aotc-chip-${e.id}`}>AOTC</span>
                        )}
                      </div>
                      <div className="mt-2 flex items-center justify-end gap-1">
                        {/* The eye: a way to READ this expense, before the pencil and shown to
                            everyone who can see the row — a beneficiary, who cannot edit, has
                            no other way in. (ZEN-702) */}
                        <ViewButton onClick={() => setViewing(e)} testid={`view-expense-${e.id}`} />
                        {isOwner && (e.status === "draft" ? (
                          <button onClick={() => { setEditing(e); setDlg(true); }} data-testid={`classify-expense-${e.id}`}
                            className="rounded-md bg-[#034AA2] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#023B82]">Classify</button>
                        ) : (
                          <button onClick={() => { setEditing(e); setDlg(true); }} data-testid={`edit-expense-${e.id}`} className="rounded p-1.5 text-[#034AA2] hover:bg-[#E9E9EB]"><PencilSimple size={16} weight="bold" /></button>
                        ))}
                        {isOwner && !e.is_split_parent && (
                          <button onClick={() => setSplitTarget(e)} data-testid={`split-expense-${e.id}`} className="rounded p-1.5 text-[#6b7280] hover:bg-[#E9E9EB]" title="Split expense"><ListChecks size={16} weight="bold" /></button>
                        )}
                        {isOwner && (
                          <button onClick={() => del(e.id)} data-testid={`delete-expense-${e.id}`} className="rounded p-1.5 text-[#F01919] hover:bg-[#F01919]/10"><Trash size={16} weight="bold" /></button>
                        )}
                      </div>
                    </div>
                  </div>
                  {attachPanel.isOpen(k) && (
                    <AttachmentPanel
                      id={attachPanel.panelId(k)}
                      attachments={atts}
                      recordLabel={expenseLabel(e)}
                      onClose={() => attachPanel.close(k)}
                      testid={`exp-attach-panel-${k}`}
                    />
                  )}
                </Card>
              );
            })}
          </div>
          {/* Mobile footer totals */}
          <div className="mt-3 rounded-xl border-2 border-[#C2C9D1] bg-[#F0F2F5] px-4 py-2.5 space-y-1.5" data-testid="expenses-footer">
            {/* Same rule as the desktop footer — no dollar figure on money
                nobody has classified. (ZEN-113) */}
            {onWaitingTab ? (
              <div className="text-xs text-[#6b7280]" data-testid="waiting-room-no-total">
                {baseList.length} waiting · no total, because none of these has been classified yet
              </div>
            ) : (<>
              <div className="flex items-center justify-between gap-2" data-testid="total-all">
                <span className="text-xs font-semibold text-[#374151]">{eligibleOnly ? `529-eligible (${baseList.length})` : hideReconciled ? `Reconciled hidden (${baseList.length})` : status ? `${effStatusLabel} — total (${baseList.length})` : listedLabel(baseList.length)}</span>
                <span className="tnum text-xs font-bold text-[#1f2937]">{money(totalAll, "USD")}</span>
              </div>
              {totalScheduled > 0.005 && (
                <div className="flex items-center justify-end gap-1.5" data-testid="exp-total-scheduled-mobile">
                  <span className="text-xs text-[#9aa1a9]">Paid ·</span>
                  <span className="tnum text-xs font-semibold text-[#1f2937]">{money(totalPaid, "USD")}</span>
                  <span className="ml-2 text-xs text-[#9aa1a9]">Not paid yet ·</span>
                  <span className="tnum text-xs font-semibold text-[#B45309]">{money(totalScheduled, "USD")}</span>
                </div>
              )}
              {activeTab === TAB_529 && allYears && <AllYearsNote testid="all-years-note-mobile" />}
              {activeTab === TAB_529 && <UntiedWithdrawalReminder withdrawals={untied} onTie={tieUntied} otherYears={untiedElsewhere} onSeeYear={seeUntiedYear} plan={reminderPlan} testid="untied-reminder-mobile" />}
              <HomeCurrencyEstimate estimate={homeEstExpenses?.estimate} />
            </>)}
          </div>
        </div>
        </>
      )}

      {isOwner && (
        <ExpenseDialog open={dlg} onClose={() => setDlg(false)} onSaved={refresh} expense={editing} defaultStudentId={effStudent} />
      )}
      <RecordView
        open={!!viewing}
        onClose={() => setViewing(null)}
        title={RECORD_TITLES.expense}
        subtitle={viewing ? expenseLabel(viewing) : ""}
        fields={viewing ? expenseFields(viewing, { parties, psSources, students: allStudents, student: studentObj, withdrawals: studentWithdrawals }) : []}
        /* The way through to the form, ONLY for someone the pencil would show
           to. Absent, not disabled, for anyone else. */
        testid="expense-view"
      />


      {selectMode && selected.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-center px-4 pb-4" data-testid="bulk-action-bar">
          <div className="flex w-full max-w-sm flex-col items-center gap-2 rounded-xl border border-[#C2C9D1] bg-white px-5 py-4 shadow-2xl">
            <span className="text-sm font-semibold text-[#6b7280]" data-testid="bulk-selected-count">{selected.length} expenses selected</span>
            <div className="flex w-full gap-2">
              <Btn variant="ghost" onClick={() => setDeleteConfirm(true)} disabled={delBusy} data-testid="bulk-delete-btn" className="h-11 flex-1 border-[#F01919]/40 text-[#F01919] hover:bg-[#F01919]/10"><Trash size={16} weight="bold" /> {delBusy ? "Deleting…" : `Delete ${selected.length}`}</Btn>
            </div>
            <Btn variant="ghost" onClick={exitSelect} data-testid="bulk-cancel-btn" className="h-11 w-full">Cancel</Btn>
          </div>
        </div>
      )}


      {/* Single expense delete confirmation */}
      <DeleteConfirmDialog open={!!singleDeleteId} onClose={() => setSingleDeleteId(null)} onConfirm={doSingleDelete}
        title="Delete expense?" description="This will permanently remove this expense. This cannot be undone." />

      {/* Bulk delete confirmation */}
      <DeleteConfirmDialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)} onConfirm={doDeleteSelected} busy={delBusy}
        title={`Delete ${selected.length} expense${selected.length === 1 ? "" : "s"}?`}
        description={<>This will permanently remove <strong>{selected.length}</strong> expense{selected.length === 1 ? "" : "s"}. This cannot be undone.</>} />

      {splitTarget && (
        // The beneficiary comes from the EXPENSE, not from whoever is selected
        // in the toolbar. In the all-beneficiaries view nothing is selected, so
        // passing the selection handed the dialog no beneficiary at all — and
        // the family's non-529 category list, which lives on the beneficiary,
        // came up empty on a screen that requires choosing from it.
        <SplitExpenseDialogLoader expenseRow={splitTarget} fallbackStudent={studentObj}
          onClose={() => setSplitTarget(null)} onDone={() => { setSplitTarget(null); refresh(); }} />
      )}
    </div>
  );
}

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { curCode, isUSD } from "@/lib/currencies";


export const LEGEND = [
  // The old wording described a scanned-receipt draft. That was already wrong:
  // the only thing in the whole app that puts an expense in this state is a
  // spreadsheet import where the 529-eligibility answer was left blank. It is
  // also the first thing an owner clicks to understand a tab they have not seen
  // before, so it has to describe what is actually in there. (ZEN-113)
  { k: "draft", desc: `An expense you haven't finished deciding on — a receipt you approved, or one brought in from a spreadsheet where the 529-eligibility answer was left blank. It counts toward nothing, and appears in no total and no tax report, until you answer. You'll find these under “${STATUS_META.draft.label}” on this page.` },
  // Second, not last, and not alphabetical: the list reads top to bottom as a
  // lifecycle, and a scheduled expense is fully decided but has not happened yet —
  // so it sits between the undecided one and the one awaiting money. Placement
  // and wording are the owner's, 2026-08-26, and the wording is verbatim.
  //
  // The last sentence is not a caveat invented here. The sweep that promotes an
  // arrived expense (helpers.settle_arrived_scheduled) refuses in exactly two
  // cases, and it leaves the row Scheduled and out of every total in both:
  //
  //   * no category — ZEN-62/ZEN-120, which landed the same day as this. That
  //     door has nobody in front of it (an ordinary page load runs the sweep),
  //     so it cannot refuse with a message; leaving the row Scheduled IS the
  //     message, and this paragraph is the only place a person is told so.
  //   * the exchange rate never settled — the app will not let a provisional
  //     dollar figure into a tax number.
  //
  // AND THE OPENING HAD TO SOFTEN WITH IT — twice over, because the first
  // softening was not enough and the second was found by auditing every clause
  // in this paragraph against the code rather than patching the one that was
  // reported. A paragraph that contradicts itself is worse than one that is
  // merely incomplete: the reader cannot tell which half to believe, and the
  // half they are living in is the half the opening denies.
  //
  //   * "have not paid yet" was stated flatly. For an expense held here PAST its
  //     date, the family has very likely paid it — and the app agrees: matching
  //     computes `paid = not is_future_dated(expense)` and never consults the
  //     status (distribution_backing). So the status does not mean "unpaid".
  //
  //     It does not mean "not counted ANYWHERE" either, which is where the next
  //     draft overshot: this very paragraph goes on to say a reserved expense DOES
  //     consume tax-credit room, and a matched one shows in the distribution's
  //     backing. Both are real totals that a Scheduled expense enters. The one
  //     thing it stays out of is money SPENT, so that is the claim it makes.
  //   * "appears in no tax total UNTIL THAT DATE ARRIVES" implied the money
  //     joins the totals on the date. A held row is past its date and still out
  //     of every total — that is the whole mechanism of the hold. It is now
  //     bounded by the STATUS ("for as long as it stays Scheduled"), which is
  //     what actually governs.
  //
  // NAMING BOTH IS THE POINT, not completeness for its own sake. An earlier
  // draft said the exchange rate was the only thing that holds an expense here,
  // which was true when written and stopped being true when this branch was
  // rebased onto the category rule. A family reading it would sit and wait for a
  // rate that is not coming, when the fix is a category they can type in ten
  // seconds. One of the two is theirs to act on; the copy has to say which.
  //
  // THE TWO "IF"s IN THE MIDDLE ARE LOAD-BEARING AND WERE ADDED AFTER REVIEW.
  // The first draft of this promised both things unconditionally, and neither is
  // unconditional:
  //
  //   * The two are ALTERNATIVES for the same dollars, never both. The same cost
  //     cannot support a tax-free 529 withdrawal and the American Opportunity
  //     Tax Credit. An earlier draft joined them with "and", which reads as
  //     though an expense could do both. Owner correction 2026-08-26.
  //
  //     THE WORDING STATES THE TAX RULE, NOT A GUARANTEE THE APP ENFORCES, and
  //     the difference is deliberate. The first attempt at this correction said
  //     the dollars "cannot also be drawn from the 529", which reads as a
  //     promise the software keeps — and it does not. The protection today is
  //     only in AGGREGATE (routes/scholarships.py: AQEE = total_qualified -
  //     tax_free_assistance - aotc_llc_reserved). Per EXPENSE there is none:
  //     refuse_reason never asks about aotc_reserved, so the picker will offer a
  //     reserved expense and the write path will accept it. Closing that is ZEN-334,
  //     owner ruling to offer only the unreserved remainder. Until it lands, the
  //     panel must not tell a family the app is stopping something it is not.
  //   * Tax-credit room is consumed by a RESERVATION, not by a status —
  //     helpers.aotc_reserved_total counts rows with `aotc_reserved: True` and
  //     never looks at `status`. ZEN-181 fixed the OPPOSITE mistake: an expense the
  //     owner HAD reserved was being dropped from the count merely because it
  //     was unpaid. It did not make every scheduled expense count.
  //   * Matching needs more than an open status. distribution_backing.refuse_reason
  //     still requires `eligible_529`, the same beneficiary, and the same calendar
  //     year; `scheduled` joining OPEN_STATUSES in ZEN-158 removed one obstacle,
  //     it did not grant the match. It ALSO refuses `payor_ref == "529"` outright —
  //     an expense the plan paid the school directly cannot be reimbursed on top —
  //     which is what "you are paying it yourself" is saying in plain words.
  //   * And the arrival is not decided by the four answers alone once a match
  //     exists. check_scheduled_status_update reads the allocations and whether
  //     the money has landed, so the SAME four answers can arrive at Awaiting, In
  //     progress, Reconciled or Closed depending on what has been matched. The
  //     four answers decide it only for an expense nothing is matched to.
  //
  // Read unconditionally, this paragraph tells a family that recording next
  // month's rent has already spent part of a credit worth up to $2,500 a year.
  // Owner ruling 2026-08-26 after the pre-merge review raised it. (ZEN-314)
  { k: "scheduled", desc: "An expense you have recorded that is not counted as money you have spent yet — normally because its date is still in the future and you have not paid it. For as long as it stays Scheduled it appears in no tax total, but it is not ignored: if you have reserved it against your tax credit, those dollars are counted as spoken for straight away — and the same dollars cannot be claimed twice: it is either the tax credit or a tax-free 529 withdrawal, never both. The app holds you to this: a dollar reserved for the tax credit is never offered for, or matched to, a 529 withdrawal. Otherwise, if it is 529-eligible and you are paying it yourself rather than the plan paying the school directly, you can match a 529 distribution to it in advance (same calendar year only). On the day its date arrives it normally leaves Scheduled on its own: if you have already matched a 529 distribution to it, it settles into whatever status that match has reached; otherwise into whatever status the four answers above give it. Two things can keep it here past its date, and only one of them is yours to fix: if the expense has no category, it stays Scheduled until you give it one; and if the exchange rate for that day could not be fetched, it stays Scheduled rather than let a provisional dollar figure into your tax numbers." },
  { k: "confirmed", desc: "Confirmed and marked for reimbursement, but no money has moved yet. Applies to any expense that will be paid back — from the 529 or via a private settlement between parties. It leaves Awaiting two ways: a 529 reimbursement moves to In progress once it's allocated to a 529 distribution; a private settlement goes straight to Reconciled when you record it." },
  { k: "in_progress", desc: "A 529 reimbursement that's been allocated to one or two 529 distributions, but the Date Received hasn't been entered on each yet. Once it has, the expense moves to Reconciled automatically — or, for direct pay (payor \"student\" or \"529\"), straight to Closed, since there's no party settlement to make. Private settlements don't pass through here — they go from Awaiting straight to Reconciled." },
  { k: "reconciled", desc: "A reimbursement has been received and matched to this expense. For a 529 expense, this means the distribution's Date Received has been entered; for a private expense, a settlement has been recorded between the parties. Owner-paid 529 expenses stay here until the party-to-party settlement (in the 529 Settlements section of the Overview) is also recorded, at which point they advance to Closed. Any expense settled privately — whether it's 529-eligible but deliberately chosen to be reimbursed outside the plan, or non-eligible altogether (e.g., a parent covering varsity-sport fees) — reaches Reconciled the same way when the private settlement is recorded. Note: 529-direct expenses (where the plan paid the institution directly) start at Reconciled — the beneficiary was never out of pocket — and move to In progress when allocated, then to Closed when the Date Received is entered." },
  { k: "closed", desc: "Done; no further action needed. Reached by every leg settling — the 529 distributed and/or the parties squared up — or by the expense never being reimbursed (paid out of pocket and absorbed; an informational record only). Direct-pay expenses (payor \"student\" or \"529\") reach Closed straight from In progress, skipping Reconciled. Both cases display as a single Closed badge." },
  // ★ ADDED BECAUSE THE TAG STARTED SAYING IT. (ZEN-635.) Splitting an expense
  // retires the original and records it as `split`. Until now nothing on any
  // screen could name that word, and Money Trace — which lists retired
  // originals on purpose, so a reader can follow what happened — printed it
  // raw. Naming the tag meant this panel owed a family an explanation of it,
  // which is the existing guard in statusDisplay.test.jsx doing its job.
  { k: "split", desc: "An expense you divided into parts. The original is kept so you can see where the parts came from, but it counts toward nothing — the parts carry the money now, and each one is decided on its own. You'll find it on Money Trace rather than in this list." },
];

// The status filter, exported so a test can read it. Hidden on purpose:
//   * "Draft" — the settled tabs exclude unclassified expenses, so it would
//     empty both and claim nothing has been filed (ZEN-113).
//   * "Split" — this list excludes split originals, so it could never match a
//     row; they are on Money Trace (owner ruling 2026-09-27).
// A status this list cannot show belongs in HIDDEN_FROM_FILTER, with why.
export const HIDDEN_FROM_FILTER = ["draft", "split"];

export function StatusFilter({ value, onChange }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid="filter-status"
      className="h-9 rounded-md border border-[#C2C9D1] bg-white px-2 text-sm font-semibold">
      <option value="">All statuses</option>
      {Object.entries(STATUS_META)
        .filter(([k]) => !HIDDEN_FROM_FILTER.includes(k))
        .map(([k, m]) => <option key={k} value={k}>{m.short}</option>)}
    </select>
  );
}

export function StatusLegend() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-4 rounded-xl border border-[#C2C9D1] bg-white" data-testid="status-legend">
      <button onClick={() => setOpen(!open)} data-testid="status-legend-toggle" className="flex w-full items-center justify-between px-4 py-2.5 text-left">
        <span className="flex items-center gap-2 text-sm font-semibold text-[#1f2937]"><Question size={16} weight="bold" className="text-[#034AA2]" /> What do the statuses mean?</span>
        <CaretDown size={16} weight="bold" className={`text-[#6b7280] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-[#C2C9D1] px-4 py-3">
          <p className="mb-3 text-xs text-[#6b7280]">Status is worked out for you — from the four answers on the expense (<b>who paid</b> · <b>is it 529-eligible?</b> · <b>use the 529?</b> · <b>reimbursed by whom?</b>), from its date, and from what has happened to the money since: a 529 distribution matched, a Date Received entered, a settlement recorded. Editing those four answers is the usual way to change it.</p>
          <ul className="space-y-2">
            {LEGEND.map(({ k, desc }) => (
                <li key={k} className="flex items-start gap-2.5 text-xs" data-testid={`legend-${k}`}>
                  <StatusChip status={k} full />
                  <span className="text-[#374151]">{desc}</span>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}


// ── ZEN-115: the split card asks, in order, and answers nothing for you ──────
//
// A part of a split is a NEW expense decision, not an echo of the receipt it
// came from. This dialog used to default every part's 529-eligibility and
// "draw from the 529" to the parent's answers and offer only the 529-eligible
// category list — so a part could be filed as eligible, drawn from the 529,
// and labelled with an IRS category without anybody deciding any of it. The
// one screen whose job is separating 529 spending from everything else had a
// hole straight through that wall.
//
// Now each part is asked in dependency order and a later question does not
// exist until the one it depends on is answered: eligibility first (blank),
// then the category list that FOLLOWS from it, then the amount, and the "draw
// from the 529?" question only when the part is eligible — absent, not
// disabled, because an impossible combination must not be expressible.
const SPLIT_MAX_PARTS = 20;

function newPart() {
  return {
    key: (crypto.randomUUID ? crypto.randomUUID() : String(Math.random())),
    eligible: "",          // "" | "yes" | "no" — blank until the owner decides
    draw: "",              // "" | "yes" | "no"
    category: "",
    amount: "",
    computerOk: false,
  };
}

function SplitExpenseDialogLoader({ expenseRow, fallbackStudent, onClose, onDone }) {
  // One fetch, keyed on the expense's own beneficiary. When the toolbar already
  // has that beneficiary loaded this resolves instantly from cache.
  const sid = expenseRow?.student_id;
  const { data: student, isLoading } = useQuery({
    queryKey: ["student", sid],
    queryFn: async () => (await api.get(`/students/${sid}`)).data,
    enabled: !!sid,
    // A category added in Settings in another tab shows when you come back.
    // (Adding one from this dialog no longer leaves it: the shared box updates
    // this beneficiary's list itself — ZEN-512.) The app-wide default disables
    // refetch-on-focus, so this query asks for it by name.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
  const resolved = student || (fallbackStudent?.id === sid ? fallbackStudent : null);
  if (!resolved) {
    return (
      <Dialog open onOpenChange={o => !o && onClose()}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Split this expense</DialogTitle></DialogHeader>
          <p className="py-6 text-center text-sm text-[#6b7280]">
            {isLoading ? "Loading this beneficiary’s categories…" : "Could not load this beneficiary’s categories."}
          </p>
          <DialogFooter><Btn variant="ghost" onClick={onClose}>Close</Btn></DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  return <SplitExpenseDialog expense={expenseRow} studentObj={resolved} onClose={onClose} onDone={onDone} />;
}

function SplitExpenseDialog({ expense, studentObj, onClose, onDone }) {
  // The education group comes from the EXPENSE, not from whichever school is
  // active today: an archived or changed school period must not quietly move a
  // recorded cost into another group's category rules mid-split. The school
  // lookup is only a fallback for older rows saved before the field existed.
  const enrollments = studentObj?.schools || [];
  const linkedSchool = enrollments.find(s => s.id === expense.school_enrollment_id);
  const group = expense.expense_group || linkedSchool?.expense_group || "post_secondary";
  const eligibleCats = irsCategoriesFor(group);
  const familyCats = studentObj?.noneligible_categories || [];

  const [parts, setParts] = useState(() => [newPart(), newPart()]);
  const [busy, setBusy] = useState(false);
  // WHICH PART the shared add-a-category box is open on, if any — one at a
  // time. The parts as they are NOW, for a save that answers after the part
  // was removed or its 529 answer changed: then nothing is chosen. (ZEN-512)
  const [addCatFor, setAddCatFor] = useState(null);
  const partsNow = useRef(parts);
  partsNow.current = parts;

  const cur = curCode(expense.original_currency, "USD");
  const cents = (v) => Math.round((parseFloat(v) || 0) * 100);
  const parentCents = cents(expense.original_amount);
  const allocatedCents = parts.reduce((s, p) => s + cents(p.amount), 0);
  const diffCents = parentCents - allocatedCents;

  // One transition per part, so a changed answer cannot leave a stale one
  // behind it: the category was chosen from a list that no longer applies, and
  // the draw answer was given about a part that is no longer eligible.
  const setPart = (key, patch) => setParts(prev => prev.map(p => {
    if (p.key !== key) return p;
    const next = { ...p, ...patch };
    if (patch.eligible !== undefined && patch.eligible !== p.eligible) {
      next.category = "";
      next.draw = "";
      next.computerOk = false;
    }
    if (patch.category !== undefined && patch.category !== "Computers/Technology") {
      next.computerOk = false;
    }
    return next;
  }));
  const addPart = () => setParts(prev => prev.length >= SPLIT_MAX_PARTS ? prev : [...prev, newPart()]);
  const removePart = (key) => setParts(prev => prev.filter(p => p.key !== key));

  // A part can hold the tax-credit reservation only if it is 529-eligible, is
  // NOT drawing from the 529 (the app never shows the reserve control on a
  // drawn expense), is a category the credit reaches, and — for a computer —
  // carries the course-of-study confirmation the edit screen asks for.
  const isQualified = (p) =>
    group === "post_secondary" && p.eligible === "yes" && p.draw === "no" &&
    AOTC_ELIGIBLE_CATEGORIES.has(p.category) &&
    (!AOTC_COMPUTER_GATE_CATEGORIES.has(p.category) || p.computerOk);

  const reservedAmt = expense.aotc_reserved ? (expense.aotc_reserved_amount || 0) : 0;
  const hasReservation = !!expense.aotc_reserved;
  const qualifiedParts = parts.filter(isQualified);
  const partDecided = (p) => p.eligible !== "" && !!p.category && cents(p.amount) > 0 &&
    (p.eligible === "no" || p.draw !== "");
  const allDecided = parts.every(partDecided);
  // Only a fully-decided split can be declared unable to hold the reservation —
  // otherwise the owner would see a red block while still filling the form in.
  const aotcBlocked = hasReservation && allDecided && qualifiedParts.length === 0;

  const valid = parts.length >= 2 && parts.length <= SPLIT_MAX_PARTS &&
    allDecided && diffCents === 0 && !aotcBlocked;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const res = await api.post(`/expenses/${expense.id}/split`, {
        children: parts.map(p => ({
          category: p.category,
          original_amount: Math.round(parseFloat(p.amount) * 100) / 100,
          eligible_529: p.eligible === "yes",
          ...(p.eligible === "yes" ? { draw_from_529: p.draw === "yes" } : {}),
          ...(p.computerOk ? { aotc_course_use_confirmed: true } : {}),
        })),
      });
      // The server is the one that knows how much of the reservation actually
      // moved — another expense may have taken part of the credit room since
      // this dialog opened. Report what happened, never assume it.
      // AND THE YEAR DECISION: splitting a cost whose year followed a
      // withdrawal re-makes that decision per piece — silence here would move
      // totals and tax documents backward with a green checkmark. (ZEN-331)
      if (res?.data?.year_note) {
        toast.warning(res.data.year_note, { duration: 10000 });
      }
      const moved = res?.data?.aotc_moved;
      if (hasReservation && moved != null && moved < reservedAmt) {
        // The case that most needs saying is the one where NOTHING moved:
        // another expense took the credit room while this dialog was open, and
        // a generic success message would let the whole reservation vanish in
        // silence.
        toast.success(
          moved > 0
            ? `Split into ${parts.length} expenses — ${money(moved, "USD")} of the ${money(reservedAmt, "USD")} tax-credit reservation moved; the rest of your credit is already used up`
            : `Split into ${parts.length} expenses — none of the ${money(reservedAmt, "USD")} tax-credit reservation could move; your credit for this year is already fully used`,
          { duration: 8000 });
      } else if (hasReservation && moved != null) {
        toast.success(`Split into ${parts.length} expenses — ${money(moved, "USD")} moved to the tax-credit reservation`);
      } else {
        toast.success(`Split into ${parts.length} expenses`);
      }
      onDone();
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  const landingLine = (p) => {
    if (p.eligible === "no") return "This part will land in Non-529 expenses — outside your 529 tax math.";
    if (p.eligible === "yes" && p.draw === "yes") return "This part will land in 529-eligible expenses and draw from the 529.";
    if (p.eligible === "yes" && p.draw === "no") return "This part will land in 529-eligible expenses, paid without drawing from the 529.";
    return null;
  };

  const selCls = "mt-0.5 block h-9 w-full rounded-md border border-[#C2C9D1] bg-white px-2 text-sm focus:border-[#034AA2] focus:outline-none focus:ring-2 focus:ring-[#034AA2]/30";

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Split this expense</DialogTitle>
        </DialogHeader>
        <div className="rounded-md border border-[#C2C9D1] bg-[#F8FAFC] p-3 text-sm">
          <div className="font-semibold">{expense.payee}</div>
          <div className="text-xs text-[#6b7280]">
            {fmtDate(expense.transaction_date)} · {money(expense.original_amount, cur)}
            {!isUSD(cur) && <> · {money(expense.usd_amount, "USD")}</>}
            {expense.category && <> · {expense.category}</>}
          </div>
          <p className="mt-1.5 text-xs text-[#374151]">
            Answer each part on its own — nothing is carried over from the original expense.
          </p>
        </div>

        <div className="mt-3 space-y-3">
          {parts.map((p, i) => {
            // Same shared rule as every other category control: null until the
            // part's own 529 question is answered, then the list that answer
            // selects. (ZEN-282)
            const cats = categoryChoicesFor({ eligible: p.eligible === "" ? null : p.eligible === "yes",
                                              expenseGroup: group, familyCategories: familyCats }) || [];
            const landing = landingLine(p);
            return (
              <div key={p.key} className="rounded-md border border-[#C2C9D1] p-3" data-testid={`split-child-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-[#6b7280]">Part {i + 1}</span>
                  {parts.length > 2 && (
                    <button onClick={() => removePart(p.key)} className="text-[#F01919] text-xs font-semibold hover:underline">Remove</button>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {/* 1 — eligibility, blank until decided */}
                  <label className="block text-xs font-semibold text-[#6b7280]">Is this part 529-eligible? *
                    <select value={p.eligible} onChange={e => setPart(p.key, { eligible: e.target.value })}
                      className={selCls} data-testid={`split-elig-${i}`}>
                      <option value="">— Decide —</option>
                      <option value="yes">Yes — 529-eligible</option>
                      <option value="no">No</option>
                    </select>
                  </label>

                  {/* 2 — category, only once eligibility is answered */}
                  {p.eligible !== "" && (
                    <label className="block text-xs font-semibold text-[#6b7280] sm:col-span-2">Category *
                      <select value={p.category} onChange={e => {
                        const v = e.target.value;
                        if (v === ADD_CATEGORY_OPTION) { setAddCatFor(p.key); return; }
                        setPart(p.key, { category: v });
                      }} className={selCls} data-testid={`split-cat-${i}`}>
                        <option value="">— Select —</option>
                        {cats.map(c => <option key={c} value={c}>{c}</option>)}
                        {p.eligible === "no" && <option value={ADD_CATEGORY_OPTION}>{ADD_CATEGORY_LABEL}</option>}
                      </select>
                      {p.eligible === "no" && familyCats.length === 0 && addCatFor !== p.key && (
                        <span className="mt-1 block font-normal text-[11px] text-[#D97706]" data-testid={`split-no-family-cats-${i}`}>
                          {NO_FAMILY_CATEGORIES_HINT}
                        </span>
                      )}
                    </label>
                  )}
                  {p.eligible === "no" && addCatFor === p.key && (
                    <div className="sm:col-span-2">
                      <AddCategoryInline
                        student={studentObj}
                        testid={`split-cat-add-${i}`}
                        isStillWanted={() => partsNow.current.some((q) => q.key === p.key && q.eligible === "no")}
                        onCancel={() => setAddCatFor(null)}
                        onChosen={(stored) => { setPart(p.key, { category: stored }); setAddCatFor(null); }} />
                    </div>
                  )}

                  {/* 3 — amount. Rendered after the category so the card reads in the
                      order it asks in: eligibility, then category, then amount. */}
                  <label className="block text-xs font-semibold text-[#6b7280]">Amount *
                    <MoneyInput currency={cur} size="sm" className="mt-0.5 !h-9"
                      value={p.amount}
                      onChange={v => setPart(p.key, { amount: v })}
                      testid={`split-amt-${i}`} />
                  </label>

                  {/* 4 — draw question, ONLY when the part is 529-eligible */}
                  {p.eligible === "yes" && (
                    <label className="block text-xs font-semibold text-[#6b7280] sm:col-span-2">Draw from the 529 account? *
                      <select value={p.draw} onChange={e => setPart(p.key, { draw: e.target.value })}
                        className={selCls} data-testid={`split-draw-${i}`}>
                        <option value="">— Decide —</option>
                        <option value="yes">Yes — pay this from the 529</option>
                        <option value="no">No — pay this another way</option>
                      </select>
                    </label>
                  )}
                </div>

                {/* The computer question the edit screen asks, asked here too —
                    a reservation must not land on a computer part without it. */}
                {hasReservation && p.category === "Computers/Technology" && p.eligible === "yes" && p.draw === "no" && (
                  <label className="mt-2 flex items-start gap-2 rounded-md border border-[#C2C9D1] bg-[#F8FAFC] px-2.5 py-1.5">
                    <input type="checkbox" checked={p.computerOk} className="mt-0.5"
                      onChange={e => setPart(p.key, { computerOk: e.target.checked })}
                      data-testid={`split-computer-check-${i}`} />
                    <span className="text-[11px] text-[#374151]">Was this computer or technology needed for a course of study?</span>
                  </label>
                )}

                {landing && (
                  <p className="mt-2 text-[11px] text-[#6b7280]" data-testid={`split-landing-${i}`}>{landing}</p>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <button onClick={addPart} disabled={parts.length >= SPLIT_MAX_PARTS}
              className="text-xs font-semibold text-[#034AA2] hover:underline disabled:text-[#9aa1a9] disabled:no-underline"
              data-testid="split-add-child">
              + Add another part
            </button>
            {parts.length >= SPLIT_MAX_PARTS && (
              <span className="ml-2 text-[11px] text-[#6b7280]">Maximum {SPLIT_MAX_PARTS} parts</span>
            )}
          </div>
          <div className={`text-sm font-bold tnum ${diffCents === 0 ? "text-[#077A39]" : diffCents > 0 ? "text-[#D97706]" : "text-[#F01919]"}`} data-testid="split-total">
            {diffCents === 0
              ? `Balanced — ${money(expense.original_amount, cur)} allocated`
              : diffCents > 0
                ? `Remaining: ${money(diffCents / 100, cur)}`
                : `Over by ${money(-diffCents / 100, cur)}`}
          </div>
        </div>

        {hasReservation && (
          aotcBlocked ? (
            <div className="mt-2 rounded-md border border-[#F01919]/30 bg-[#F01919]/5 p-2.5" data-testid="split-aotc-block">
              <p className="text-xs font-semibold text-[#B91C1C]">
                This split leaves no part that can hold the {money(reservedAmt, "USD")} you reserved for the tax credit.
                Remove or reduce the reservation to continue.
              </p>
            </div>
          ) : (
            <p className="mt-2 text-[11px] text-[#374151]" data-testid="split-aotc-line">
              {qualifiedParts.length === 0
                ? `This expense has ${money(reservedAmt, "USD")} reserved for the tax credit — at least one part must be able to keep it.`
                : qualifiedParts.length === 1
                  ? `Up to ${money(reservedAmt, "USD")} reserved for the tax credit will move to the ${qualifiedParts[0].category} part.`
                  : `Up to ${money(reservedAmt, "USD")} reserved for the tax credit will spread across the ${qualifiedParts.map(q => q.category).join(", ")} parts, filling them in order.`}
            </p>
          )
        )}

        <DialogFooter className="mt-3">
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn enterPrimary onClick={submit} disabled={!valid || busy} data-testid="split-confirm">
            {busy ? "Splitting…" : `Split into ${parts.length}`}
          </Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}